import './FileDropZone.scss';

import React, { useRef, useState } from 'react';
import { FlightButton } from '@flybits/design-system';

import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import { CSV_UPLOAD_FILE_SIZE_LIMIT_LABEL } from 'constants/shared';

export type TFileDropZoneProps = {
  allowedTypes?: string[];
  multiple?: boolean;
  disabled?: boolean;
  showDownloadSampleFile?: boolean;
  sampleFileHref?: string;
  onFilesSelected: (files: FileList) => void;
  isBusy?: boolean;
  totalFiles?: number;
  filesUploaded?: number;
  fileUploadedPercentage?: number;
};

const MAIN_CLASS = 'file-drop-zone';
const CLASSES = {
  AREA: `${MAIN_CLASS}__area`,
  AREA_DRAGGING: `${MAIN_CLASS}__area--is-dragging`,
  INPUT: `${MAIN_CLASS}__input`,
  TITLE: `${MAIN_CLASS}__title`,
  SUBTITLE: `${MAIN_CLASS}__subtitle`,
  ACTIONS: `${MAIN_CLASS}__actions`,
  BROWSE: `${MAIN_CLASS}__browse`,
  SAMPLE_FILE: `${MAIN_CLASS}__sample-file`,
  SPINNER: `${MAIN_CLASS}__spinner`,
};

const DEFAULT_SAMPLE_FILE_HREF = '/assets/Context-Data-Upload-Template.csv';

const FileDropZone: React.FC<TFileDropZoneProps> = ({
  allowedTypes,
  multiple = true,
  disabled = false,
  showDownloadSampleFile = false,
  sampleFileHref = DEFAULT_SAMPLE_FILE_HREF,
  onFilesSelected,
  isBusy = false,
  totalFiles = 0,
  filesUploaded = 0,
  fileUploadedPercentage = 0,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isDisabled = disabled || isBusy;
  const displayedTotalFiles = totalFiles || 1;

  const handleFiles = (files: FileList | null) => {
    if (!files?.length || isDisabled) return;
    onFilesSelected(files);
  };

  const handleFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(evt.target.files);
    evt.target.value = '';
  };

  const handleDrop = (evt: React.DragEvent<HTMLDivElement>) => {
    evt.preventDefault();
    setIsDragging(false);
    handleFiles(evt.dataTransfer.files);
  };

  const handleDragLeave = (evt: React.DragEvent<HTMLDivElement>) => {
    evt.preventDefault();
    setIsDragging(false);
  };

  const handleDragOver = (evt: React.DragEvent<HTMLDivElement>) => {
    evt.preventDefault();
    if (isDisabled) return;
    setIsDragging(true);
  };

  const handleBrowseClick = () => {
    inputRef.current?.click();
  };

  return (
    <div className={MAIN_CLASS}>
      <div
        className={`${CLASSES.AREA}${isDragging ? ` ${CLASSES.AREA_DRAGGING}` : ''}`}
        data-testid="file-drop-zone"
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDragEnd={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          className={CLASSES.INPUT}
          type="file"
          accept={allowedTypes?.join(',')}
          disabled={isDisabled}
          onChange={handleFileChange}
          multiple={multiple}
          aria-label="select a .csv file to upload"
        />
        <p className={CLASSES.TITLE}>Drag and drop files here</p>
        <p className={CLASSES.SUBTITLE}>CSV files up to {CSV_UPLOAD_FILE_SIZE_LIMIT_LABEL}</p>
        <div className={CLASSES.ACTIONS}>
          <FlightButton
            className={CLASSES.BROWSE}
            type="button"
            label="Browse Files"
            size="small"
            theme="primary"
            disabled={isDisabled}
            onClick={handleBrowseClick}
          />
          {showDownloadSampleFile && (
            <a className={CLASSES.SAMPLE_FILE} href={sampleFileHref} download>
              Download Sample File
            </a>
          )}
        </div>
      </div>
      {isBusy && (
        <div className={CLASSES.SPINNER}>
          <LoadingIcon />
          {`Your ${
            displayedTotalFiles > 1 ? 'files are' : 'file is'
          } being uploaded (${fileUploadedPercentage}%)... (${filesUploaded}/${displayedTotalFiles})`}
        </div>
      )}
    </div>
  );
};

export default FileDropZone;
